// Press-feedback scale for the map's round controls (#436).
//
// Locate-me and the card-drawer toggle shrink to 93% while they are held and spring back on
// release. The first version wired `pointerdown` / `pointerup` / `pointerleave` inline on each
// button and stopped there.
//
// `pointercancel` fires INSTEAD OF `pointerup` when the browser takes the gesture away — a
// scroll or a system edge swipe claims it, a call arrives, the page is hidden — and once it has
// fired the pointer is gone, so no `pointerleave` follows either. The inline `scale(0.93)` was
// therefore never cleared: the button stayed visibly smaller than its neighbour until the next
// complete press. Measured at 390x844: `pointerdown` then `pointercancel` left
// `transform = "scale(0.93)"` stuck on the element.
//
// The handler set lives here rather than inline in src/App.js for two reasons: the reset is then
// written once for every control that opts in (the two buttons had two copies of it, which is
// how one of them could have been fixed and the other missed), and the behaviour can be
// unit-tested without mounting Google Maps. `src/map/pressScaleWiring.test.js` asserts App.js
// really spreads this set onto both controls, because a correct module nobody uses fixes nothing.

export const PRESSED_TRANSFORM = 'scale(0.93)';
export const RESTING_TRANSFORM = 'scale(1)';

// A pointer handler that throws takes the whole map render down for a cosmetic effect, so a
// missing or already-released `currentTarget` is a no-op rather than a crash. React reuses and
// nulls synthetic event fields, and these handlers can be invoked from a test or a probe with a
// bare object.
function setTransform(event, transform) {
  const target = event && event.currentTarget;
  if (!target || !target.style) return;
  target.style.transform = transform;
}

// Frozen module constant rather than a factory: the handlers are stateless, so one shared object
// keeps every render handing React the same function identities.
export const pressScaleHandlers = Object.freeze({
  onPointerDown: event => setTransform(event, PRESSED_TRANSFORM),
  onPointerUp: event => setTransform(event, RESTING_TRANSFORM),
  // The fix. `pointercancel` is not a rarer `pointerup` — it is the branch the browser takes
  // when it has decided the gesture belongs to something else, and it is the only notification
  // the element gets.
  onPointerCancel: event => setTransform(event, RESTING_TRANSFORM),
  onPointerLeave: event => setTransform(event, RESTING_TRANSFORM),
});

export default pressScaleHandlers;
